import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Button, Checkbox, EmptyState, Field, Modal, Notice, PageHeader, RowActions, Select, StatusIndicator, Table, Tag } from '@voice/ui';
import { CredentialsPanel } from '../components/CredentialsPanel';
import { ApiError, api } from '../lib/api';
import type { DeviceCredentials, DeviceView, ExtensionView, ExtensionsResponse, Forwarding, UsersResponse } from '../lib/types';
import styles from './Pages.module.css';

const KEY = ['extensions'] as const;

export function Extensions({ canManageUsers }: { canManageUsers: boolean }) {
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const query = useQuery({ queryKey: KEY, queryFn: () => api<ExtensionsResponse>('/extensions'), refetchInterval: 10_000 });
  const editing = query.data?.extensions.find((e) => e.id === editingId);

  return (
    <div className={styles.page}>
      <PageHeader title="Nebenstellen">
        <Button onClick={() => setCreating(true)}>Neue Nebenstelle</Button>
      </PageHeader>

      {query.isPending && <p className={styles.note}>Lädt …</p>}
      {query.isError && <Notice tone="error">Die Nebenstellen konnten nicht geladen werden.</Notice>}
      {query.data && query.data.extensions.length === 0 && <EmptyState>Noch keine Nebenstellen. Lege die erste an, z. B. 101.</EmptyState>}
      {query.data && query.data.extensions.length > 0 && (
        <Table aria-label="Nebenstellen">
          <thead>
            <tr>
              <th>Nummer</th>
              <th>Name</th>
              <th>Benutzer</th>
              <th>Geräte</th>
              <th>Zustand</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {query.data.extensions.map((e) => {
              const online = e.devices.filter((d) => d.registrations.length > 0).length;
              return (
                <tr key={e.id}>
                  <th scope="row">{e.number}</th>
                  <td>{e.displayName}</td>
                  <td>{e.owner ?? <span className={styles.muted}>–</span>}</td>
                  <td>
                    <StatusIndicator state={online > 0 ? 'ok' : 'off'} label={`${online} von ${e.devices.length} angemeldet`} />
                  </td>
                  <td>
                    <span className={styles.inline}>
                      {e.dnd && <Tag tone="warn">Nicht stören</Tag>}
                      {(e.forwarding.always || e.forwarding.busy || e.forwarding.noAnswer) && <Tag>Umleitung</Tag>}
                    </span>
                  </td>
                  <td>
                    <RowActions>
                      <Button variant="secondary" onClick={() => setEditingId(e.id)}>
                        Bearbeiten
                      </Button>
                    </RowActions>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {creating && <CreateExtension canManageUsers={canManageUsers} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); setEditingId(id); }} />}
      {editing && query.data && <EditExtension key={editing.id} extension={editing} connection={query.data.connection} canManageUsers={canManageUsers} onClose={() => setEditingId(null)} />}
    </div>
  );
}

function errorText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Etwas ist schiefgelaufen.';
}

function UserSelect({ value, onChange, enabled }: { value: string; onChange: (v: string) => void; enabled: boolean }) {
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<UsersResponse>('/users'), enabled });
  if (!enabled) return null;
  return (
    <Select label="Benutzer" value={value} onChange={(e) => onChange(e.target.value)} hint="Dieser Benutzer kann Nicht-stören und Umleitungen selbst einstellen.">
      <option value="">Niemand</option>
      {users.data?.users.map((u) => (
        <option key={u.id} value={u.id}>
          {u.name}
        </option>
      ))}
    </Select>
  );
}

function CreateExtension({ canManageUsers, onClose, onCreated }: { canManageUsers: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  const [number, setNumber] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [userId, setUserId] = useState('');
  const create = useMutation({
    mutationFn: () => api<{ id: string }>('/extensions', { method: 'POST', body: { number, displayName, userId: userId || null } }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: KEY });
      onCreated(r.id);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <Modal open onClose={onClose} title="Neue Nebenstelle" size="sm">
      <form className={styles.form} onSubmit={submit}>
        <Field label="Rufnummer" inputMode="numeric" value={number} onChange={(e) => setNumber(e.target.value.replace(/\D/g, ''))} placeholder="101" required autoFocus />
        <Field label="Name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Anna Beispiel" required />
        <UserSelect value={userId} onChange={setUserId} enabled={canManageUsers} />
        {create.isError && <Notice tone="error">{errorText(create.error)}</Notice>}
        <div className={styles.inline} style={{ justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={!number || !displayName || create.isPending}>
            Anlegen
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EditExtension({ extension, connection, canManageUsers, onClose }: { extension: ExtensionView; connection: ExtensionsResponse['connection']; canManageUsers: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });

  const [displayName, setDisplayName] = useState(extension.displayName);
  const [userId, setUserId] = useState(extension.userId ?? '');
  const [callerId, setCallerId] = useState(extension.outboundCallerId ?? '');
  const [allowExternal, setAllowExternal] = useState(extension.allowExternal);
  const [dnd, setDnd] = useState(extension.dnd);
  const [fwd, setFwd] = useState<Forwarding>(extension.forwarding);
  const [credentials, setCredentials] = useState<DeviceCredentials | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api('/extensions/' + extension.id, {
        method: 'PATCH',
        body: { displayName, userId: userId || null, outboundCallerId: callerId || null, allowExternal, dnd, forwarding: { always: fwd.always ?? '', busy: fwd.busy ?? '', noAnswer: fwd.noAnswer ?? '', ...(fwd.noAnswerSeconds ? { noAnswerSeconds: fwd.noAnswerSeconds } : {}) } },
      }),
    onSuccess: refresh,
  });
  const addDevice = useMutation({
    mutationFn: () => api<DeviceCredentials>(`/extensions/${extension.id}/devices`, { method: 'POST', body: { label: newLabel } }),
    onSuccess: async (c) => {
      setCredentials(c);
      setNewLabel('');
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () => api('/extensions/' + extension.id, { method: 'DELETE' }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });

  if (credentials) {
    return (
      <Modal open onClose={onClose} title="Zugangsdaten des Geräts" description={`Nebenstelle ${extension.number}`} size="md" footer={<Button onClick={() => setCredentials(null)}>Fertig</Button>}>
        <CredentialsPanel credentials={credentials} displayName={extension.displayName} />
      </Modal>
    );
  }

  const numField = (key: 'always' | 'busy' | 'noAnswer', label: string, hint?: string) => (
    <Field
      label={label}
      inputMode="numeric"
      value={fwd[key] ?? ''}
      onChange={(e) => setFwd({ ...fwd, [key]: e.target.value.replace(/\D/g, '') })}
      placeholder="Nebenstelle, z. B. 102"
      {...(hint ? { 'aria-describedby': undefined } : {})}
    />
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={`Nebenstelle ${extension.number}`}
      description={extension.displayName}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Schließen
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <div className={styles.row}>
          <Field label="Name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          <Field label="Angezeigte Nummer nach außen" value={callerId} onChange={(e) => setCallerId(e.target.value)} placeholder="leer = Hauptnummer" />
        </div>
        <UserSelect value={userId} onChange={setUserId} enabled={canManageUsers} />
        <Checkbox label="Darf nach extern telefonieren" checked={allowExternal} onChange={(e) => setAllowExternal(e.target.checked)} />
        <Checkbox label="Nicht stören" hint="Anrufer hören besetzt bzw. werden umgeleitet." checked={dnd} onChange={(e) => setDnd(e.target.checked)} />

        <div>
          <h3 className={styles.heading}>Rufumleitung</h3>
          <div className={styles.row}>
            {numField('always', 'Immer umleiten auf')}
            {numField('busy', 'Bei besetzt umleiten auf')}
            {numField('noAnswer', 'Bei Nichtmelden umleiten auf')}
            <Field
              label="Nach Sekunden (Nichtmelden)"
              inputMode="numeric"
              value={fwd.noAnswerSeconds ?? ''}
              onChange={(e) => setFwd({ ...fwd, ...(e.target.value ? { noAnswerSeconds: Number(e.target.value.replace(/\D/g, '')) } : { noAnswerSeconds: undefined }) })}
              placeholder="25"
            />
          </div>
        </div>
        {save.isError && <Notice tone="error">{errorText(save.error)}</Notice>}
        {save.isSuccess && <Notice tone="success">Gespeichert.</Notice>}

        <div>
          <h3 className={styles.heading}>Geräte</h3>
          {extension.devices.length === 0 ? (
            <p className={styles.note}>Noch kein Gerät. Füge eines hinzu, um mit MicroSIP oder einem Tischtelefon zu telefonieren.</p>
          ) : (
            <div className={styles.deviceList}>
              {extension.devices.map((d) => (
                <DeviceRow key={d.id} device={d} extension={extension} onChanged={refresh} onCredentials={setCredentials} />
              ))}
            </div>
          )}
          <form
            className={styles.inline}
            style={{ marginTop: 16 }}
            onSubmit={(e) => {
              e.preventDefault();
              addDevice.mutate();
            }}
          >
            <Field label="Neues Gerät" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="z. B. MicroSIP Büro" style={{ flex: '1 1 240px' }} />
            <Button type="submit" variant="secondary" disabled={!newLabel || addDevice.isPending} style={{ alignSelf: 'flex-end' }}>
              Hinzufügen
            </Button>
          </form>
          {addDevice.isError && <Notice tone="error">{errorText(addDevice.error)}</Notice>}
          <p className={styles.muted} style={{ marginTop: 8 }}>
            Zugangsdaten für den Browser-Client ({connection.domain}) legst du hier ebenfalls als Gerät an.
          </p>
        </div>

        <div>
          <h3 className={styles.heading}>Nebenstelle löschen</h3>
          {confirmDelete ? (
            <div className={styles.inline}>
              <span>Wirklich löschen? Alle Geräte werden abgemeldet.</span>
              <Button variant="secondary" onClick={() => remove.mutate()} disabled={remove.isPending}>
                Ja, löschen
              </Button>
              <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                Nein
              </Button>
            </div>
          ) : (
            <Button variant="secondary" onClick={() => setConfirmDelete(true)}>
              Löschen …
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function DeviceRow({ device, extension, onChanged, onCredentials }: { device: DeviceView; extension: ExtensionView; onChanged: () => void; onCredentials: (c: DeviceCredentials) => void }) {
  const reset = useMutation({ mutationFn: () => api<DeviceCredentials>(`/devices/${device.id}/reset-password`, { method: 'POST' }), onSuccess: (c) => { onCredentials(c); onChanged(); } });
  const toggle = useMutation({ mutationFn: () => api(`/devices/${device.id}`, { method: 'PATCH', body: { active: !device.active } }), onSuccess: onChanged });
  const remove = useMutation({ mutationFn: () => api(`/devices/${device.id}`, { method: 'DELETE' }), onSuccess: onChanged });
  const reg = device.registrations[0];
  return (
    <div className={styles.device}>
      <div className={styles.deviceInfo}>
        <span className={styles.deviceName}>
          {device.label} <span className={styles.mono}>({device.username})</span>
        </span>
        <span className={styles.muted}>
          {!device.active ? 'Deaktiviert' : reg ? `Angemeldet über ${reg.via}${reg.userAgent ? ` · ${reg.userAgent}` : ''}` : 'Nicht angemeldet'}
        </span>
      </div>
      <RowActions>
        <Button variant="secondary" onClick={() => reset.mutate()} disabled={reset.isPending}>
          Neues Passwort
        </Button>
        <Button variant="secondary" onClick={() => toggle.mutate()} disabled={toggle.isPending}>
          {device.active ? 'Deaktivieren' : 'Aktivieren'}
        </Button>
        <Button variant="secondary" onClick={() => { if (confirm(`Gerät „${device.label}“ von Nebenstelle ${extension.number} löschen?`)) remove.mutate(); }} disabled={remove.isPending}>
          Löschen
        </Button>
      </RowActions>
    </div>
  );
}
